import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import toast from "react-hot-toast";
import {
  Drawer,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Close } from "@dreamhub/designsystem/icons";
import { DrawerTitle as CustomDrawerTitle } from "./components/DrawerTitle";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import { APIFieldItem, SettingsFieldType } from "@/pages/settings/types";
import { useFieldDisplayTypeTargets } from "@/pages/settings/hooks";
import { useFieldForm } from "./hooks/useFieldForm";
import { FieldFormContent } from "./components/FieldFormContent";
import { FieldFormFooter } from "./components/FieldFormFooter";
import { IconButton } from "@/components/ui/icon-button";

const TRANSLATION_KEY_TABS = "settings.deals";

interface EditCustomFieldDrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  fieldData: APIFieldItem | null;
  entityType: EntityType;
  onSave?: (fieldData?: any) => void;
  onDelete?: (fieldId: string) => void;
}

export const EditCustomFieldDrawer: React.FC<EditCustomFieldDrawerProps> = ({
  isOpen,
  onOpenChange,
  fieldData,
  entityType,
  onSave,
  onDelete
}) => {
  const { t } = useTranslation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTypeChangeModalOpen, setIsTypeChangeModalOpen] = useState(false);
  const [isDeleteConfirmed, setIsDeleteConfirmed] = useState(false);

  const displayTypeTargetsResult = useFieldDisplayTypeTargets(
    entityType,
    fieldData?.id,
    isOpen && fieldData?.fieldType === SettingsFieldType.CUSTOM
  );
  const displayTypeTargets = displayTypeTargetsResult.data ?? null;

  const {
    formState,
    isFormValid,
    formRef,
    formSetValue,
    setFormSetValue,
    setShowHelpText,
    setShowInCreation,
    setIsRequired,
    handleFormValid,
    handleOptionsManagerChange,
    handleFormSubmit,
    handleSubmitButton,
    isSubmitting
  } = useFieldForm({
    fieldData,
    entityType: entityType,
    isEditMode: true
  });

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleDeleteClick = () => {
    setIsDeleteConfirmed(false);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (fieldData) {
      onDelete?.(fieldData.id);
      setIsDeleteModalOpen(false);
      handleClose();
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const isDisplayTypeChanged =
    !!fieldData && formState.formData.fieldType !== fieldData.displayType;

  const submitForm = async () => {
    try {
      await handleFormSubmit();
      onSave?.();
      handleClose();
    } catch (error) {
      console.error("Failed to update custom field:", error);
      const backendDetail =
        isAxiosError(error) && typeof error.response?.data?.detail === "string"
          ? error.response.data.detail
          : null;
      toast.error(
        backendDetail ??
          t(`${TRANSLATION_KEY_TABS}.fieldManagement.updateField.error`)
      );
    }
  };

  const handleSave = async () => {
    // Converting a populated field rewrites its values on every record —
    // require an explicit confirmation before applying.
    if (isDisplayTypeChanged && displayTypeTargets?.isPopulated) {
      setIsTypeChangeModalOpen(true);
      return;
    }
    await submitForm();
  };

  const handleConfirmTypeChange = async () => {
    setIsTypeChangeModalOpen(false);
    await submitForm();
  };

  const handleCancelTypeChange = () => {
    setIsTypeChangeModalOpen(false);
  };

  const getTitle = () => {
    return <CustomDrawerTitle title="Edit Field" subtitle="Custom field" />;
  };

  if (!fieldData) {
    return null;
  }

  return (
    <>
      <Drawer
        open={isOpen}
        onOpenChange={onOpenChange}
        direction="right"
        modal={false}
        dismissible={false}
      >
        <AppDrawerContent isSettingsDrawer>
          <DrawerHeader>
            <DrawerTitle>
              {getTitle()}
            </DrawerTitle>
            <IconButton
              data-test="close-edit-field-drawer"
              onClick={handleClose}
              variant="iconNoBg"
              size="md"
            >
              <Close />
            </IconButton>
          </DrawerHeader>

          <FieldFormContent
            formData={formState.formData}
            optionsManagerData={formState.optionsManagerData}
            fieldData={fieldData}
            formSetValue={formSetValue}
            showHelpText={formState.showHelpText}
            showInCreation={formState.showInCreation}
            isRequired={formState.isRequired}
            onShowHelpTextChange={setShowHelpText}
            onShowInCreationChange={setShowInCreation}
            onRequiredChange={setIsRequired}
            onOptionsManagerChange={handleOptionsManagerChange}
            onFormValid={handleFormValid}
            onSubmit={handleSave}
            formRef={formRef}
            onSetValue={setFormSetValue}
            isEditMode={true}
            entityType={entityType}
            displayTypeTargets={displayTypeTargets}
            shouldNotResetOnSubmit={true}
          />

          <FieldFormFooter
            isFormValid={isFormValid}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmitButton}
            onCancel={handleClose}
            onDelete={handleDeleteClick}
            isEditMode={true}
          />
        </AppDrawerContent>
      </Drawer>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        title={t(`${TRANSLATION_KEY_TABS}.fieldManagement.deleteField.title`)}
        confirmationText={t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.deleteField.confirmationText`,
          { entitiesName: entityTypeToEntityName[entityType] }
        )}
        handleSubmit={handleConfirmDelete}
        handleCancel={handleCancelDelete}
        submitButtonText={t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.deleteField.submitButton`
        )}
        cancelButtonText={t("cancel")}
        checkboxLabel={t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.deleteField.checkboxLabel`
        )}
        isCheckboxRequired
        onCheckboxChange={setIsDeleteConfirmed}
        isCheckboxChecked={isDeleteConfirmed}
      />

      <ConfirmationModal
        isOpen={isTypeChangeModalOpen}
        setIsOpen={setIsTypeChangeModalOpen}
        title={t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.changeFieldType.title`
        )}
        confirmationText={t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.changeFieldType.confirmationText`
        )}
        handleSubmit={handleConfirmTypeChange}
        handleCancel={handleCancelTypeChange}
        submitButtonText={t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.changeFieldType.submitButton`
        )}
        cancelButtonText={t("cancel")}
      />
    </>
  );
};
